import React, { useEffect, useState } from "react";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import tw from "twrnc";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";

import EventSettingsScreenContainer from "@/components/EventSettingsScreenContainer";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import {
  fetchViewerHoursMonth,
  fetchViewerHoursMonths,
} from "@/components/http/viewerHours";
import {
  FAIR_USE_HOURS,
  formatHours,
  monthMoment,
} from "@/components/viewerHours/logic";
import { t, tn } from "@/localization";
import { useAuth } from "../../AuthContext";

// Profile › Viewer-hours: the account's CDN viewer-hours per UTC month, and
// each month's events, so a videographer sees which events used more than the
// 5,000 viewer-hours each event includes. An event's hours past its 5,000 are
// "extra" in the month they were watched (US$20 per 1,000, mkrn/eventlive.pro#97).
// Business accounts; the numbers: components/viewerHours,
// eventlive-cdk/lambda/viewerHours.ts.

const hoursLabel = (hours) =>
  tn("{count} viewer-hour", "{count} viewer-hours", hours === 1 ? 1 : 2, {
    count: formatHours(hours),
  });

const Pill = ({ tone, icon, children }) => (
  <View
    style={[
      tw`flex-row items-center self-start px-2 py-0.5 mt-1`,
      {
        borderRadius: radius.pill,
        backgroundColor: tone === "warning" ? colors.warningTint : colors.surfaceStrong,
      },
    ]}
  >
    {!!icon && (
      <Ionicons
        name={icon}
        size={13}
        color={tone === "warning" ? colors.warning : colors.muted}
        style={tw`mr-1`}
      />
    )}
    <Text style={[text.hint, tone === "warning" && { color: colors.warning }]}>
      {children}
    </Text>
  </View>
);

const EventRow = ({ event, onOpen }) => {
  const title = event.title || event.slug || t("Untitled event");
  const date = event.event_datetime_utc
    ? moment(event.event_datetime_utc).format(t("MMM D, YYYY"))
    : null;
  const elsewhere = Math.abs((event.event_hours || 0) - (event.hours || 0)) >= 0.05;
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => onOpen(event)}
      disabled={!event.slug}
      style={({ hovered, pressed }) => [
        tw`flex-row items-center px-3 py-3`,
        { borderTopWidth: 1, borderTopColor: colors.divider },
        (hovered || pressed) && { backgroundColor: colors.surfaceStrong },
      ]}
    >
      <View style={tw`flex-1 pr-3`}>
        <Text style={text.bodyStrong} numberOfLines={2}>
          {title}
        </Text>
        {!!date && <Text style={text.hint}>{date}</Text>}
        {event.extra_hours > 0 ? (
          <Pill tone="warning" icon="alert-circle">
            {t("{extra} over {limit} this month", {
              extra: formatHours(event.extra_hours),
              limit: formatHours(FAIR_USE_HOURS),
            })}
          </Pill>
        ) : event.over_fair_use ? (
          <Pill tone="warning" icon="alert-circle">
            {t("Over {limit} in all", { limit: formatHours(FAIR_USE_HOURS) })}
          </Pill>
        ) : event.ticketed ? (
          <Pill icon="ticket-outline">{t("Ticketed: no limit")}</Pill>
        ) : null}
      </View>
      <View style={tw`items-end`}>
        <Text style={text.bodyStrong}>{formatHours(event.hours)}</Text>
        {elsewhere && (
          <Text style={text.hint}>
            {t("{hours} in all", { hours: formatHours(event.event_hours) })}
          </Text>
        )}
      </View>
      {!!event.slug && (
        <Ionicons
          name="chevron-forward"
          size={16}
          color={colors.subtle}
          style={tw`ml-2`}
        />
      )}
    </Pressable>
  );
};

const MonthCard = ({ month, current, onOpenEvent }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(undefined); // undefined: not loaded
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && detail === undefined) {
      setDetail("loading");
      fetchViewerHoursMonth(month.month).then((d) => setDetail(d || null));
    }
  };
  const events =
    detail && detail !== "loading"
      ? [...(detail.events || [])].sort((a, b) => (b.hours || 0) - (a.hours || 0))
      : [];
  const name = monthMoment(month.month).format(t("MMMM YYYY"));
  return (
    <View
      style={[
        tw`mb-3`,
        {
          borderWidth: 1,
          borderColor: colors.divider,
          borderRadius: radius.card,
          backgroundColor: colors.white,
          overflow: "hidden",
        },
      ]}
    >
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        onPress={toggle}
        style={({ hovered, pressed }) => [
          tw`flex-row items-center p-4`,
          (hovered || pressed) && { backgroundColor: colors.surface },
        ]}
      >
        <View style={tw`flex-1 pr-3`}>
          <Text style={text.label}>
            {current ? t("{month} (so far)", { month: name }) : name}
          </Text>
          <Text style={[text.hint, tw`mt-0.5`]}>
            {tn("{count} event", "{count} events", month.events_count || 0)}
            {" · "}
            {t("Live {live} · Replay {replay}", {
              live: formatHours(month.live_hours),
              replay: formatHours(month.replay_hours),
            })}
          </Text>
          {month.extra_hours > 0 && (
            <Pill tone="warning" icon="alert-circle">
              {tn(
                "{extra} extra viewer-hours on {count} event over {limit}",
                "{extra} extra viewer-hours on {count} events over {limit}",
                month.events_over_fair_use || 1,
                {
                  extra: formatHours(month.extra_hours),
                  limit: formatHours(FAIR_USE_HOURS),
                }
              )}
            </Pill>
          )}
        </View>
        <Text style={text.sectionTitle}>{hoursLabel(month.hours)}</Text>
        <Ionicons
          name={open ? "chevron-up" : "chevron-down"}
          size={18}
          color={colors.subtle}
          style={tw`ml-2`}
        />
      </Pressable>
      {open &&
        (detail === "loading" ? (
          <View style={tw`py-4`}>
            <AppActivityIndicator />
          </View>
        ) : detail === null ? (
          <Text style={[text.hint, tw`px-4 pb-4`]}>
            {t("Couldn't load this month. Please try again.")}
          </Text>
        ) : (
          events.map((e) => (
            <EventRow key={e.event_id} event={e} onOpen={onOpenEvent} />
          ))
        ))}
    </View>
  );
};

const ViewerHours = ({ navigation }) => {
  const { profileLoaded, account_type } = useAuth();
  const [months, setMonths] = useState(undefined); // undefined: loading
  const business = account_type === "business";

  useEffect(() => {
    if (!profileLoaded || !business) return;
    fetchViewerHoursMonths(24).then((m) => setMonths(m));
  }, [profileLoaded, business]);

  const thisMonth = moment.utc().format("YYYY-MM");
  const openEvent = (e) =>
    navigation.navigate("EventAnalytics", { slug: e.slug });

  return (
    <EventSettingsScreenContainer
      fallbackRoute="Profile"
      loading={!profileLoaded || (business && months === undefined)}
    >
      <SettingsPage
        title={t("Viewer-hours")}
        help="profile.viewerHours"
        description={t(
          "How many hours of your videos people watched each month, live and replay. Each event includes {limit} viewer-hours; ticketed events have no limit.",
          { limit: formatHours(FAIR_USE_HOURS) }
        )}
      >
        {!business ? (
          <Callout tone="info">
            {t("Viewer-hours are shown to business accounts.")}
          </Callout>
        ) : months === null ? (
          <Callout tone="danger">
            {t("Couldn't load viewer-hours. Please try again later.")}
          </Callout>
        ) : !months?.length ? (
          <Callout tone="info">
            {t("No viewer-hours yet. They're updated every hour, within an hour of people watching.")}
          </Callout>
        ) : (
          <View>
            {months.map((m) => (
              <MonthCard
                key={m.month}
                month={m}
                current={m.month === thisMonth}
                onOpenEvent={openEvent}
              />
            ))}
            <Text style={[text.hint, tw`mt-2`]}>
              {t(
                "Counted every hour from the video our CDN delivered, on every player. Months are in UTC, and an event's hours count in the month people watched. Test events aren't included."
              )}
            </Text>
          </View>
        )}
      </SettingsPage>
    </EventSettingsScreenContainer>
  );
};

export default ViewerHours;
